import { CellContext } from "@/lib/Components/DataTable/types";
import { RelocationListItem } from "@/app/Relocations/GraphQL/relocationListQuery";
import { cn } from "@/lib/utils";
import { Link } from "@/components/catalyst/link";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { markRelocationLineAsSoldMutation } from "@/app/Relocations/GraphQL/relocationLineReferenceMutations";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  LockIcon,
  UnlockIcon,
} from "lucide-react";
import { useState } from "react";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import { Button } from "@/components/catalyst/button";
import { Competitor } from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { Highlighter } from "@/lib/Components/Highlighter";

function formatCompetitor(competitor: Competitor | null | undefined): string {
  if (!competitor) return "";

  switch (competitor) {
    case Competitor.Transfercar:
      return "TransferCar";
    case Competitor.Coseats:
      return "CoSeats";
    case Competitor.Drivenow:
      return "DriveNow";
    case Competitor.Supplier:
      return "Withdrawn";
    default:
      return "";
  }
}

const MAX_VISIBLE_LINES = 3;

export function RelocationReferenceCell({
  row,
  table,
}: CellContext<RelocationListItem, any>) {
  const t = useTranslations("relocation.table");
  const lines = row.original.lines;
  const [isExpanded, setIsExpanded] = useState(false);
  const search = table.options.meta?.search ?? "";

  if (lines.length === 0) {
    return null;
  }

  const hasMoreLines = lines.length > MAX_VISIBLE_LINES;
  const visibleLines = isExpanded ? lines : lines.slice(0, MAX_VISIBLE_LINES);
  const hiddenCount = lines.length - MAX_VISIBLE_LINES;

  return (
    <div>
      <ul>
        {visibleLines.map((ref) => {
          let color = "bg-green-500";

          if (ref.sold_at) {
            if (ref.booking) {
              color = "bg-yellow-500";
            } else {
              color = "bg-red-500";
            }
          }

          const competitor = formatCompetitor(ref.sold_by);

          return (
            <li key={ref.id} className="flex items-center">
              <div className="flex flex-grow items-center">
                <span
                  className={cn(color, "mr-2 inline-flex h-2 w-2 rounded-full")}
                />
                <span
                  className="inline-block max-w-64 truncate align-middle"
                  title={ref.reference}
                >
                  <Highlighter searchString={search}>
                    {ref.reference}
                  </Highlighter>
                </span>
              </div>

              <div className="ml-1 flex shrink-0 items-center space-x-1">
                {competitor ? (
                  <span className="text-xs text-gray-500">({competitor})</span>
                ) : null}

                {ref.booking ? (
                  <span>
                    <Link
                      className="text-blue-500 underline"
                      to={
                        "/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
                      }
                      params={(prev) => ({
                        ...prev,
                        bookingId: ref.booking?.id,
                      })}
                    >
                      {ref.booking?.reference ?? "--"}
                    </Link>
                  </span>
                ) : null}

                <ReserveButton reference={ref} />
              </div>
            </li>
          );
        })}
      </ul>
      {hasMoreLines ? (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 flex items-center text-xs text-gray-500 hover:text-gray-700"
        >
          {isExpanded ? (
            <>
              <ChevronUpIcon className="mr-1 h-3 w-3" />
              {t("showLess")}
            </>
          ) : (
            <>
              <ChevronDownIcon className="mr-1 h-3 w-3" />
              {t("showMore", { count: hiddenCount })}
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}

function ReserveButton({ reference }: { reference: any }) {
  const t = useTranslations("relocation.table");
  const { isAdmin } = useTenant();
  const { mutateAsync, isPending } = useGqlMutation(
    markRelocationLineAsSoldMutation,
  );

  if (!isAdmin) return null;

  if (reference.sold_at) {
    return (
      <Button
        plain
        isLoading={isPending}
        LeadingIcon={UnlockIcon}
        onClick={async () => {
          await mutateAsync({
            id: reference.id,
            sold: false,
          });
        }}
        title={t("release")}
      />
    );
  }

  return (
    <Dropdown>
      <DropdownButton
        as={Button}
        plain
        isLoading={isPending}
        LeadingIcon={LockIcon}
        className="mr-0"
      />
      <DropdownMenu>
        <DropdownItem
          onClick={async () => {
            await mutateAsync({
              id: reference.id,
              sold: true,
              soldBy: Competitor.Transfercar,
            });
          }}
        >
          {t("soldByTransfercar")}
        </DropdownItem>
        <DropdownItem
          onClick={async () => {
            await mutateAsync({
              id: reference.id,
              sold: true,
              soldBy: Competitor.Coseats,
            });
          }}
        >
          {t("soldByCoseats")}
        </DropdownItem>
        <DropdownItem
          onClick={async () => {
            await mutateAsync({
              id: reference.id,
              sold: true,
              soldBy: Competitor.Drivenow,
            });
          }}
        >
          {t("soldByDrivenow")}
        </DropdownItem>
        <DropdownItem
          onClick={async () => {
            await mutateAsync({
              id: reference.id,
              sold: true,
            });
          }}
        >
          {t("soldOther")}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
